import React, { useState, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  makeStyles,
  CircularProgress,
  Divider,
  Paper
} from '@material-ui/core'
import { AttachFile, Delete, CloudDownload, CloudUpload } from '@material-ui/icons'
import { toast } from 'utils/notify'
import { storageRef } from '../index'
import moment from 'moment'
import { useDropzone } from 'react-dropzone'

const DocumentUpload = ({
  title = 'Document Attachments',
  subTitle = 'Attach documents related to this event. Allowed formats: PDF, DOCX, XLSX, PPTX, TXT (Max size: 10MB)',
  attachmentType,
  conferenceId,
  entityId,
  attachments = [],
  onAttachmentAdded,
  onAttachmentDeleted,
  onTemporaryAttachments,
  onPendingUploadsChange,
  resetKey = 0 // Add a key prop for resetting component state
}) => {
  const classes = useStyles()
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])

  // Reset selected files when resetKey changes
  React.useEffect(() => {
    if (resetKey > 0) {
      // Revoke object URLs to avoid memory leaks
      selectedFiles.forEach((file) => URL.revokeObjectURL(file.preview))
      setSelectedFiles([])
    }
  }, [resetKey])

  // Allowed file types
  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'text/plain' // .txt
  ]

  // Maximum file size (10MB)
  const maxSizeInBytes = 10 * 1024 * 1024

  // Setup dropzone
  const onDrop = useCallback(
    (acceptedFiles) => {
      // Validate files
      const validFiles = acceptedFiles.filter((file) => {
        // Check file type
        if (!allowedTypes.includes(file.type)) {
          const extension = file.name.split('.').pop().toLowerCase()
          // Additional check for common extensions in case MIME type is not correctly detected
          if (
            !(
              extension === 'pdf' ||
              extension === 'docx' ||
              extension === 'xlsx' ||
              extension === 'pptx' ||
              extension === 'txt'
            )
          ) {
            toast.warning(
              `Invalid file type: ${file.name}. Please upload PDF, DOCX, XLSX, PPTX, or TXT files.`
            )
            return false
          }
        }

        // Check file size
        if (file.size > maxSizeInBytes) {
          toast.warning(`File too large: ${file.name}. Maximum size is 10MB.`)
          return false
        }

        return true
      })

      const extensionMimeMap = {
        pdf: 'application/pdf',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        txt: 'text/plain'
      }

      // Wrap files with preview metadata without mutating the original File object
      const filesWithPreview = validFiles.map((file) => {
        const ext = file.name.split('.').pop().toLowerCase()
        const resolvedType = file.type || extensionMimeMap[ext] || 'application/octet-stream'
        return {
          file,
          name: file.name,
          size: file.size,
          type: resolvedType,
          preview: URL.createObjectURL(file),
          displaySize: formatFileSize(file.size)
        }
      })

      // Add to selected files
      setSelectedFiles((current) => {
        const updatedFiles = [...current, ...filesWithPreview]

        // If this is for a new entity (no entityId yet), send the files to parent
        if (!entityId && onTemporaryAttachments) {
          const attachmentsMetadata = updatedFiles.map((wrapper) => {
            const timestamp = new Date().getTime() + Math.floor(Math.random() * 1000)
            const originalName = wrapper.name
            const fileName = `${timestamp}_${originalName}`
            return {
              fileName: originalName,
              fileType: wrapper.type,
              fileSize: wrapper.size,
              storageFileName: fileName,
              uploadedAt: moment().valueOf(),
              path: `${conferenceId}/attachments/${attachmentType}/${fileName}`,
              file: wrapper.file // Include the actual File object
            }
          })

          // Send both metadata and file objects to parent component
          onTemporaryAttachments(attachmentsMetadata)
        }

        // Notify parent about pending uploads for existing entity
        if (entityId && onPendingUploadsChange) {
          onPendingUploadsChange(updatedFiles.length > 0)
        }

        return updatedFiles
      })
    },
    [entityId, conferenceId, attachmentType, onTemporaryAttachments, onPendingUploadsChange]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: Infinity, // Don't use dropzone's built-in size validation
    multiple: true
  })

  // Remove a file from the selected files list
  const removeSelectedFile = (fileIndex) => {
    setSelectedFiles((current) => {
      const newFiles = [...current]
      // Revoke the object URL to avoid memory leaks
      URL.revokeObjectURL(newFiles[fileIndex].preview)
      newFiles.splice(fileIndex, 1)

      // If this is for a new entity (no entityId yet), update the parent with remaining files
      if (!entityId && onTemporaryAttachments) {
        const attachmentsMetadata = newFiles.map((wrapper) => {
          const timestamp = new Date().getTime() + Math.floor(Math.random() * 1000)
          const originalName = wrapper.name
          const fileName = `${timestamp}_${originalName}`
          return {
            fileName: originalName,
            fileType: wrapper.type,
            fileSize: wrapper.size,
            storageFileName: fileName,
            uploadedAt: moment().valueOf(),
            path: `${conferenceId}/attachments/${attachmentType}/${fileName}`,
            file: wrapper.file // Include the actual File object
          }
        })

        // Send updated list to parent component
        onTemporaryAttachments(attachmentsMetadata)
      }

      // Notify parent about pending uploads for existing entity
      if (entityId && onPendingUploadsChange) {
        onPendingUploadsChange(newFiles.length > 0)
      }

      return newFiles
    })
  }

  // Upload all selected files
  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) {
      toast.info('Select at least one file to upload.')
      return
    } else if (selectedFiles.length > 500) {
      toast.error('Upload limit is 500 files at a time.')
      return
    }

    setUploading(true)
    let successCount = 0

    const attachmentsToAdd = []
    for (const wrapper of selectedFiles) {
      try {
        // Generate unique filename
        const timestamp = new Date().getTime() + Math.floor(Math.random() * 1000)
        const originalName = wrapper.name
        const fileName = `${timestamp}_${originalName}`

        // Upload to Firebase Storage
        const fileRef = storageRef.child(
          `${conferenceId}/attachments/${attachmentType}/${fileName}`
        )
        await fileRef.put(wrapper.file)
        const downloadUrl = await fileRef.getDownloadURL()

        // put attachment data to array
        const attachmentData = {
          fileName: originalName,
          fileType: wrapper.type,
          fileSize: wrapper.size,
          storageFileName: fileName,
          downloadUrl,
          uploadedAt: moment().valueOf(),
          path: `${conferenceId}/attachments/${attachmentType}/${fileName}`
        }
        attachmentsToAdd.push(attachmentData)
        successCount++
      } catch (error) {
        console.error(`Error uploading file ${wrapper.name}:`, error)
        toast.error(`Failed to upload ${wrapper.name}`)
      }
    }
    // Call onAttachmentAdded to handle Firestore update
    if (onAttachmentAdded) {
      await onAttachmentAdded(attachmentsToAdd)
    }

    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} attachment(s)`)
      // Clear selected files and their previews
      selectedFiles.forEach((file) => URL.revokeObjectURL(file.preview))
      setSelectedFiles([])

      // Notify parent that there are no more pending uploads
      if (entityId && onPendingUploadsChange) {
        onPendingUploadsChange(false)
      }
    }
    setUploading(false)
  }

  const handleDownload = (attachment) => {
    // Open the download URL in a new tab
    window.open(attachment.downloadUrl, '_blank')
  }

  const handleDelete = async (attachment) => {
    try {
      setLoading(true)
      // Delete from Firebase Storage
      if (attachment?.storageFileName) {
        await storageRef
          .child(`${conferenceId}/attachments/${attachmentType}/${attachment.storageFileName}`)
          .delete()
      }

      // Call onAttachmentDeleted to handle Firestore update
      if (onAttachmentDeleted) {
        await onAttachmentDeleted(attachment)
      }

      toast.success('File removed.')
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error('Could not delete file. Try again.')
    } finally {
      setLoading(false)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes'
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    else return (bytes / 1048576).toFixed(1) + ' MB'
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.headerBox}>
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
        <Typography variant="body2" className={classes.subTitle}>
          {subTitle}
        </Typography>
      </Box>

      {/* File drop zone */}
      <Paper
        variant="outlined"
        className={`${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ''}`}
        {...getRootProps()}>
        <input {...getInputProps()} />
        <Box className={classes.dropzoneContent}>
          <AttachFile className={classes.dropzoneIcon} />
          {isDragActive ? (
            <Typography variant="body1" className={classes.dropzoneText}>
              Drop files here...
            </Typography>
          ) : (
            <Typography variant="body1" className={classes.dropzoneText}>
              Drag & drop files here, or click to select files
            </Typography>
          )}
          <Typography variant="body2" className={classes.dropzoneSubText}>
            Supported formats: PDF, DOCX, XLSX, PPTX, TXT (Max: 10MB)
          </Typography>
        </Box>
      </Paper>

      {/* Selected files section */}
      {selectedFiles.length > 0 && (
        <Box mt={2}>
          <Card variant="outlined" className={classes.filesCard}>
            <CardContent className={classes.cardContent}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" className={classes.filesTitle}>
                  Selected Files ({selectedFiles.length})
                </Typography>
                {entityId && (
                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleUploadFiles}
                      disabled={uploading}
                      className={classes.uploadSelectedButton}
                      startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}>
                      {uploading ? 'Uploading...' : 'Upload All'}
                    </Button>
                  </Box>
                )}
              </Box>
              <Divider />
              <List dense className={classes.filesList}>
                {selectedFiles.map((file, index) => (
                  <ListItem key={index} className={classes.fileItem}>
                    <ListItemText
                      primary={file.name}
                      secondary={`${file.displaySize} • Ready to upload`}
                      className={classes.fileItemText}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        disabled={uploading}
                        onClick={() => removeSelectedFile(index)}
                        className={classes.deleteButton}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Upload action button for mobile UI */}
      {selectedFiles.length > 0 && (
        <Box mt={2} className={classes.mobileUploadButton}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleUploadFiles}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}>
            {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Files`}
          </Button>
        </Box>
      )}

      {/* Separator */}
      {selectedFiles.length > 0 && attachments.length > 0 && (
        <Divider className={classes.separator} />
      )}

      {/* Uploaded documents section */}
      {attachments.length > 0 && (
        <Card variant="outlined" className={classes.filesCard} style={{ marginTop: '16px' }}>
          <CardContent className={classes.cardContent}>
            <Typography variant="subtitle1" className={classes.filesTitle}>
              Uploaded Documents ({attachments.length})
            </Typography>
            <List dense className={classes.filesList}>
              {attachments.map((attachment, index) => (
                <ListItem key={index} className={classes.fileItem}>
                  <ListItemText
                    primary={attachment.fileName}
                    secondary={`${formatFileSize(attachment.fileSize)} • ${moment(
                      attachment.uploadedAt
                    ).format('MM/DD/YYYY hh:mm A')}`}
                    className={classes.fileItemText}
                  />
                  <ListItemSecondaryAction style={{ display: 'flex', gap: '16px' }}>
                    <IconButton
                      edge="end"
                      onClick={() => handleDownload(attachment)}
                      className={classes.downloadButton}>
                      <CloudDownload fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      disabled={loading}
                      onClick={() => handleDelete(attachment)}
                      className={classes.deleteButton}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  headerBox: {
    marginBottom: theme.spacing(2)
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: theme.spacing(0.5)
  },
  subTitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
  },
  dropzone: {
    border: '2px dashed #e0e0e0',
    borderRadius: 4,
    backgroundColor: '#fafafa',
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border .24s ease-in-out, background-color .24s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(3, 169, 244, 0.04)'
    }
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(3, 169, 244, 0.08)'
  },
  dropzoneContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dropzoneIcon: {
    color: theme.palette.primary.main,
    fontSize: 48,
    marginBottom: theme.spacing(1)
  },
  dropzoneText: {
    color: '#2c3e50',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5)
  },
  dropzoneSubText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
  },
  uploadSelectedButton: {
    textTransform: 'none'
  },
  mobileUploadButton: {
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  separator: {
    margin: theme.spacing(2, 0)
  },
  filesCard: {
    borderColor: '#e0e0e0'
  },
  cardContent: {
    padding: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(1.5)
    }
  },
  filesTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  filesList: {
    padding: 0
  },
  fileItem: {
    padding: theme.spacing(0.75, 0),
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  fileItemText: {
    marginRight: theme.spacing(8),
    '& .MuiListItemText-primary': {
      fontWeight: 500,
      fontSize: '0.9rem',
      color: theme.palette.primary.main
    },
    '& .MuiListItemText-secondary': {
      fontSize: '0.75rem'
    }
  },
  downloadButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.info.main
  },
  deleteButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}))

export default DocumentUpload
